import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Package, ShoppingBag, CreditCard } from "lucide-react";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (!loading && !user) nav("/login");
  }, [loading, user, nav]);

  useEffect(() => {
    if (user) api.get("/orders").then(r => setOrders(r.data)).catch(() => {});
  }, [user]);

  if (loading || !user) return <div className="nx-container py-24 mono text-xs">LOADING...</div>;

  const totalSpent = orders.reduce((s, o) => s + (o.total || 0), 0);

  return (
    <div className="nx-container py-10">
      <span className="mono text-[11px] text-slate-500">DASHBOARD</span>
      <div className="flex items-end justify-between mt-2 mb-8">
        <h1 className="text-4xl md:text-5xl font-black tracking-tighter">Welcome, {user.name}.</h1>
        <Link to="/products" data-testid="dashboard-shop-btn" className="nx-btn-primary">Continue Shopping</Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        <div className="border border-slate-900 p-6">
          <ShoppingBag size={20} className="text-blue-700 mb-3" />
          <p className="mono text-[10px] text-slate-500">ORDERS</p>
          <p className="text-4xl font-black tracking-tighter mt-1">{orders.length}</p>
        </div>
        <div className="border border-slate-900 p-6">
          <CreditCard size={20} className="text-blue-700 mb-3" />
          <p className="mono text-[10px] text-slate-500">TOTAL/SPENT</p>
          <p className="text-4xl font-black tracking-tighter mt-1">${totalSpent.toFixed(2)}</p>
        </div>
        <div className="border border-slate-900 p-6 bg-[#A3E635]">
          <Package size={20} className="text-emerald-900 mb-3" />
          <p className="mono text-[10px] text-emerald-900">ACCOUNT</p>
          <p className="text-xl font-black tracking-tight mt-1 truncate">{user.email}</p>
        </div>
      </div>

      <div>
        <h2 className="text-2xl font-black tracking-tighter mb-4">Recent orders</h2>
        {orders.length === 0 ? (
          <div className="border border-dashed border-slate-300 p-12 text-center">
            <p className="text-slate-500 mb-4">No orders yet.</p>
            <Link to="/products" className="nx-btn-primary inline-block">Browse Products</Link>
          </div>
        ) : (
          <div className="border border-slate-200 bg-white divide-y divide-slate-200">
            {orders.map(o => (
              <div key={o.order_id} data-testid={`order-${o.order_id}`} className="p-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <p className="mono text-[10px] text-slate-500">{o.order_id}</p>
                  <p className="text-sm mt-1">{o.items?.length || 0} items · {new Date(o.created_at).toLocaleDateString()}</p>
                </div>
                <div className="text-right">
                  <p className="font-black text-lg">${o.total?.toFixed(2)}</p>
                  <span className="nx-tag text-emerald-700 border-emerald-700">PAID</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
